import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  FileText, Plus, Search, Edit, Trash2, Mail, Send, 
  Download, ArrowRight, Check, Clock, CreditCard,
  FileCheck, Truck, Receipt, ExternalLink, Phone, Copy
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SalesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [clients, setClients] = useState([]);
  const [productsServices, setProductsServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('devis');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [transactionToConvert, setTransactionToConvert] = useState(null);
  const [transactionToEdit, setTransactionToEdit] = useState(null);
  const [convertTargetType, setConvertTargetType] = useState('bl');
  const [transactionForPayment, setTransactionForPayment] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    type: 'devis',
    client_id: '',
    chantier: '',
    title: '', // Custom title for the document
    accounting_type: 'A',
    sale_category: 'agglo', // agglo, granulats, autres
    tax_rate: 0,
    notes: '',
    document_date: new Date().toISOString().split('T')[0], // Custom document date (YYYY-MM-DD)
    items: [{ product_id: '', product_type: 'autre', description: '', quantity: 1, unit: 'unité', unit_price: 0, total: 0, purchase_cost: 0 }]
  });

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    client_id: '',
    chantier: '',
    title: '',
    accounting_type: 'A',
    sale_category: 'agglo',
    tax_rate: 0,
    notes: '',
    document_date: new Date().toISOString().split('T')[0],
    items: []
  });

  // Payment form
  const [paymentData, setPaymentData] = useState({
    payment_method: 'especes',
    payment_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([
      fetchTransactions(),
      fetchClients(),
      fetchProductsServices()
    ]);
    setLoading(false);
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/transactions`, getAuthHeaders());
      setTransactions(response.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error('Erreur lors du chargement');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, getAuthHeaders());
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchProductsServices = async () => {
    try {
      const response = await axios.get(`${API}/products-services?active_only=true`, getAuthHeaders());
      setProductsServices(response.data);
    } catch (error) {
      console.error('Error fetching products/services:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      type: activeTab,
      client_id: '',
      chantier: '',
      accounting_type: 'A',
      tax_rate: 0,
      notes: '',
      document_date: new Date().toISOString().split('T')[0],
      items: [{ product_id: '', product_type: 'autre', description: '', quantity: 1, unit: 'unité', unit_price: 0, total: 0 }]
    });
  };

  const openCreateModal = (type = activeTab) => {
    resetForm();
    setFormData(prev => ({ ...prev, type }));
    setShowCreateModal(true);
  };

  const openEditModal = (transaction) => {
    setTransactionToEdit(transaction);
    setEditFormData({
      client_id: transaction.client_id,
      chantier: transaction.chantier || '',
      accounting_type: transaction.accounting_type,
      tax_rate: transaction.tax_rate || 0,
      notes: transaction.notes || '',
      document_date: transaction.created_at ? new Date(transaction.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      items: transaction.items.map(item => ({
        product_id: item.product_id || '',
        product_type: item.product_type,
        description: item.description || '',
        quantity: item.quantity,
        unit: item.unit || 'unité',
        unit_price: item.unit_price,
        total: item.total
      }))
    });
    setShowEditModal(true);
  };

  // Open detail modal
  const openDetailModal = (transaction) => {
    setSelectedTransaction(transaction);
    setEditFormData({
      client_id: transaction.client_id,
      chantier: transaction.chantier || '',
      accounting_type: transaction.accounting_type,
      tax_rate: transaction.tax_rate || 0,
      notes: transaction.notes || '',
      document_date: transaction.created_at ? new Date(transaction.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      items: transaction.items.map(item => ({
        product_id: item.product_id || '',
        product_type: item.product_type,
        description: item.description || '',
        quantity: item.quantity,
        unit: item.unit || 'unité',
        unit_price: item.unit_price,
        total: item.total
      }))
    });
    setIsEditing(false);
    setShowDetailModal(true);
  };

  // Save edited transaction from detail modal
  const handleSaveFromDetail = async () => {
    if (!selectedTransaction) return;
    
    setSaving(true);
    try {
      const payload = {
        client_id: editFormData.client_id,
        chantier: editFormData.chantier,
        accounting_type: editFormData.accounting_type,
        tax_rate: parseFloat(editFormData.tax_rate) || 0,
        notes: editFormData.notes,
        items: editFormData.items.map(item => ({
          product_id: item.product_id || null,
          product_type: item.product_type || 'autre',
          description: item.description,
          quantity: parseFloat(item.quantity) || 0,
          unit: item.unit || 'unité',
          unit_price: parseFloat(item.unit_price) || 0,
          total: parseFloat(item.quantity) * parseFloat(item.unit_price) || 0
        }))
      };

      if (editFormData.document_date) {
        payload.created_at = new Date(`${editFormData.document_date}T12:00:00Z`).toISOString();
      }

      const response = await axios.put(`${API}/transactions/${selectedTransaction.id}/full`, payload, getAuthHeaders());
      toast.success('Document modifié avec succès');
      
      // Update transactions list
      await fetchTransactions();
      
      // Update selected transaction with response data
      if (response.data) {
        setSelectedTransaction(response.data);
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating transaction:', error);
      const errDetail = error.response?.data?.detail;
      toast.error(Array.isArray(errDetail) ? errDetail.map(d => d.msg || d).join(', ') : (errDetail || 'Erreur'));
    } finally {
      setSaving(false);
    }
  };

  // Duplicate a transaction
  const handleDuplicate = (transaction) => {
    // Pre-fill the form with data from the transaction to duplicate
    setFormData({
      type: transaction.type, // Keep the same type (devis, bl, facture)
      client_id: transaction.client_id,
      chantier: transaction.chantier || '',
      accounting_type: transaction.accounting_type,
      tax_rate: transaction.tax_rate || 0,
      notes: transaction.notes || '',
      items: transaction.items.map(item => ({
        product_id: item.product_id || '',
        product_type: item.product_type,
        description: item.description || '',
        quantity: item.quantity,
        unit: item.unit || 'unité',
        unit_price: item.unit_price,
        total: item.total
      }))
    });
    setShowCreateModal(true);
    toast.info(`Duplication du ${getTypeLabel(transaction.type)} ${transaction.reference}`);
  };

  // Item management for create form
  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { product_id: '', product_type: 'autre', description: '', quantity: 1, unit: 'unité', unit_price: 0, total: 0 }]
    }));
  };

  // Item management for edit form
  const addEditItem = () => {
    setEditFormData(prev => ({
      ...prev,
      items: [...prev.items, { product_id: '', product_type: 'autre', description: '', quantity: 1, unit: 'unité', unit_price: 0, total: 0 }]
    }));
  };

  const removeEditItem = (index) => {
    if (editFormData.items.length > 1) {
      setEditFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const updateEditItem = (index, field, value) => {
    setEditFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      if (field === 'quantity' || field === 'unit_price') {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(newItems[index].quantity) || 0;
        const price = field === 'unit_price' ? parseFloat(value) || 0 : parseFloat(newItems[index].unit_price) || 0;
        newItems[index].total = qty * price;
      }
      
      return { ...prev, items: newItems };
    });
  };

  const calculateEditTotals = () => {
    const subtotal = editFormData.items.reduce((sum, item) => sum + (item.total || 0), 0);
    const taxAmount = subtotal * (editFormData.tax_rate / 100);
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const getAggloProductType = (name) => {
    const n = (name || '').toLowerCase();
    if (n.includes('hourdis')) return 'agglo_hourdis_16';
    if (n.includes('plein')) return 'agglo_plein_15';
    const m = n.match(/\b(10|15|20)\b/);
    return m ? `agglo_${m[1]}` : 'agglo_10';
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      // If selecting a product from catalog, auto-fill details
      if (field === 'product_id' && value) {
        const product = productsServices.find(p => p.id === value);
        if (product) {
          newItems[index].description = product.name;
          newItems[index].unit = product.unit;
          newItems[index].unit_price = product.unit_price;
          newItems[index].product_type = product.category === 'agglo' ? getAggloProductType(product.name) : 
                                          product.category === 'granulat' ? 'granulat' :
                                          product.category === 'transport' ? 'transport' : 'autre';
          // Recalculate total
          const qty = parseFloat(newItems[index].quantity) || 0;
          newItems[index].total = qty * product.unit_price;
        }
      }
      
      // Auto-calculate total
      if (field === 'quantity' || field === 'unit_price') {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(newItems[index].quantity) || 0;
        const price = field === 'unit_price' ? parseFloat(value) || 0 : parseFloat(newItems[index].unit_price) || 0;
        newItems[index].total = qty * price;
      }
      
      // Calculate margin for granulat and transport
      if (field === 'purchase_cost' || field === 'quantity' || field === 'unit_price') {
        const qty = parseFloat(newItems[index].quantity) || 0;
        const purchaseCost = parseFloat(newItems[index].purchase_cost) || 0;
        if (purchaseCost > 0) {
          newItems[index].cost_total = qty * purchaseCost;
          newItems[index].margin = newItems[index].total - newItems[index].cost_total;
        }
      }
      
      return { ...prev, items: newItems };
    });
  };

  // Similar update for edit form
  const updateEditItemWithProduct = (index, field, value) => {
    setEditFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      if (field === 'product_id' && value) {
        const product = productsServices.find(p => p.id === value);
        if (product) {
          newItems[index].description = product.name;
          newItems[index].unit = product.unit;
          newItems[index].unit_price = product.unit_price;
          newItems[index].product_type = product.category === 'agglo' ? getAggloProductType(product.name) : 
                                          product.category === 'granulat' ? 'granulat' :
                                          product.category === 'transport' ? 'transport' : 'autre';
          const qty = parseFloat(newItems[index].quantity) || 0;
          newItems[index].total = qty * product.unit_price;
        }
      }
      
      if (field === 'quantity' || field === 'unit_price') {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(newItems[index].quantity) || 0;
        const price = field === 'unit_price' ? parseFloat(value) || 0 : parseFloat(newItems[index].unit_price) || 0;
        newItems[index].total = qty * price;
      }
      
      return { ...prev, items: newItems };
    });
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.total || 0), 0);
    const taxAmount = subtotal * (formData.tax_rate / 100);
    const total = subtotal + taxAmount;
    return { subtotal, taxAmount, total };
  };

  const handleSave = async () => {
    if (!formData.client_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    if (formData.items.some(item => !item.unit_price || !item.quantity)) {
      toast.error('Veuillez remplir tous les articles (prix et quantité)');
      return;
    }

    setSaving(true);
    try {
      // Ensure all numeric values are properly parsed
      const cleanedItems = formData.items.map(item => ({
        product_type: item.product_type || 'autre',
        description: item.description || '',
        quantity: parseFloat(item.quantity) || 0,
        unit: item.unit || 'unité',
        unit_price: parseFloat(item.unit_price) || 0,
        total: parseFloat(item.total) || (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)
      }));

      const payload = {
        type: formData.type,
        client_id: formData.client_id,
        chantier: formData.chantier || '',
        title: formData.title || '',
        accounting_type: formData.accounting_type || 'A',
        sale_category: formData.sale_category || 'agglo',
        tax_rate: parseFloat(formData.tax_rate) || 0,
        notes: formData.notes || '',
        items: cleanedItems
      };

      if (formData.document_date) {
        payload.created_at = new Date(`${formData.document_date}T12:00:00Z`).toISOString();
      }

      await axios.post(`${API}/transactions`, payload, getAuthHeaders());
      toast.success(`${getTypeLabel(formData.type)} créé avec succès`);
      setShowCreateModal(false);
      resetForm();
      await fetchTransactions();
    } catch (error) {
      console.error('Error saving transaction:', error);
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : (typeof detail === 'string' ? detail : 'Erreur lors de l\'enregistrement');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleEditSave = async () => {
    if (!transactionToEdit) return;
    
    if (!editFormData.client_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    if (editFormData.items.some(item => !item.unit_price || !item.quantity)) {
      toast.error('Veuillez remplir tous les articles');
      return;
    }

    setSaving(true);
    try {
      const editPayload = { ...editFormData };
      if (editFormData.document_date) {
        editPayload.created_at = new Date(`${editFormData.document_date}T12:00:00Z`).toISOString();
      }
      delete editPayload.document_date;
      await axios.put(
        `${API}/transactions/${transactionToEdit.id}/full`,
        editPayload,
        getAuthHeaders()
      );
      toast.success(`${getTypeLabel(transactionToEdit.type)} modifié avec succès`);
      setShowEditModal(false);
      setTransactionToEdit(null);
      fetchTransactions();
    } catch (error) {
      console.error('Error updating:', error);
      const errDetail = error.response?.data?.detail;
      toast.error(Array.isArray(errDetail) ? errDetail.map(d => d.msg || d).join(', ') : (errDetail || 'Erreur'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!transactionToDelete) return;
    try {
      await axios.delete(`${API}/transactions/${transactionToDelete.id}`, getAuthHeaders());
      toast.success('Document supprimé');
      setTransactionToDelete(null);
      fetchTransactions();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const [convertItems, setConvertItems] = useState([]);
  const [convertDate, setConvertDate] = useState(() => new Date().toISOString().split('T')[0]);

  const handleConvert = async () => {
    if (!transactionToConvert) return;
    try {
      const body = { created_at: new Date(`${convertDate}T12:00:00Z`).toISOString() };
      if (convertTargetType === 'bl' && transactionToConvert.type === 'devis' && convertItems.length > 0) {
        const items = convertItems
          .filter(it => (parseFloat(it.qty_now) || 0) > 0)
          .map(it => ({
            product_id: it.product_id || null,
            product_type: it.product_type || 'autre',
            description: it.description,
            quantity: parseFloat(it.qty_now) || 0,
            unit: it.unit || 'unité',
            unit_price: parseFloat(it.unit_price) || 0,
            total: (parseFloat(it.qty_now) || 0) * (parseFloat(it.unit_price) || 0),
          }));
        if (items.length === 0) {
          toast.error('Aucune ligne avec quantité à livrer > 0');
          return;
        }
        body.items = items;
      }
      await axios.post(
        `${API}/transactions/${transactionToConvert.id}/convert?target_type=${convertTargetType}`,
        body,
        getAuthHeaders()
      );
      toast.success(`Converti en ${getTypeLabel(convertTargetType)}`);
      setTransactionToConvert(null);
      setShowConvertModal(false);
      setDeliveryStatus(null);
      setConvertItems([]);
      fetchTransactions();
    } catch (error) {
      const errD2 = error.response?.data?.detail;
      toast.error(Array.isArray(errD2) ? errD2.map(d => d.msg || d).join(', ') : (errD2 || 'Erreur lors de la conversion'));
    }
  };

  const openConvertModal = async (transaction, defaultTarget = 'bl') => {
    setTransactionToConvert(transaction);
    const target = transaction.type === 'bl' ? 'facture' : defaultTarget;
    setConvertTargetType(target);
    setConvertDate(new Date().toISOString().split('T')[0]);
    setDeliveryStatus(null);
    setConvertItems([]);
    setShowConvertModal(true);
    // Load delivery status for devis→BL to show partial-delivery picker
    if (transaction.type === 'devis' && target === 'bl') {
      try {
        const res = await axios.get(`${API}/transactions/${transaction.id}/delivery-status`, getAuthHeaders());
        setDeliveryStatus(res.data);
        setConvertItems(res.data.items.map((it, idx) => ({
          index: idx,
          product_id: it.product_id,
          product_type: it.product_type,
          description: it.description,
          unit: it.unit,
          unit_price: it.unit_price,
          quantity: it.quantity,
          delivered: it.delivered,
          remaining: it.remaining,
          qty_now: it.remaining, // default to fully deliver remaining
        })));
      } catch (e) {
        // Non-blocking: fall back to plain convert without partial UI
      }
    }
  };

  const handleDownloadPDF = async (transaction) => {
    try {
      const response = await axios.get(
        `${API}/transactions/${transaction.id}/pdf`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AGRABAT_${getTypeLabel(transaction.type)}_${transaction.reference}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleSendEmail = async (transaction) => {
    if (!transaction.client_email) {
      toast.error('Le client n\'a pas d\'adresse email');
      return;
    }
    try {
      await axios.post(`${API}/transactions/${transaction.id}/send-email`, {}, getAuthHeaders());
      toast.success(`Email envoyé à ${transaction.client_email}`);
      fetchTransactions();
    } catch (error) {
      const errD3 = error.response?.data?.detail;
      toast.error(Array.isArray(errD3) ? errD3.map(d => d.msg || d).join(', ') : (errD3 || 'Erreur lors de l\'envoi'));
    }
  };

  const handleWhatsApp = async (transaction) => {
    if (!transaction.client_phone) {
      toast.error('Le client n\'a pas de numéro de téléphone');
      return;
    }
    try {
      const response = await axios.get(`${API}/transactions/${transaction.id}/whatsapp-link`, getAuthHeaders());
      // Utiliser location.href pour meilleure compatibilité mobile
      window.location.href = response.data.whatsapp_url;
    } catch (error) {
      const errD4 = error.response?.data?.detail;
      toast.error(Array.isArray(errD4) ? errD4.map(d => d.msg || d).join(', ') : (errD4 || 'Erreur'));
    }
  };

  const handleMarkAsPaid = async () => {
    if (!transactionForPayment) return;
    try {
      await axios.put(
        `${API}/transactions/${transactionForPayment.id}`,
        {
          status: 'paid',
          payment_method: paymentData.payment_method,
          payment_date: paymentData.payment_date
        },
        getAuthHeaders()
      );
      toast.success('Facture marquée comme payée');
      setShowPaymentModal(false);
      setTransactionForPayment(null);
      fetchTransactions();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getTypeLabel = (type) => {
    const labels = { devis: 'Devis', bl: 'Bon de Livraison', facture: 'Facture' };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: { class: 'bg-gray-100 text-gray-800', label: 'Brouillon' },
      sent: { class: 'bg-blue-100 text-blue-800', label: 'Envoyé' },
      confirmed: { class: 'bg-green-100 text-green-800', label: 'Confirmé' },
      delivered: { class: 'bg-purple-100 text-purple-800', label: 'Livré' },
      paid: { class: 'bg-emerald-100 text-emerald-800', label: 'Payé' },
      cancelled: { class: 'bg-red-100 text-red-800', label: 'Annulé' }
    };
    const style = styles[status] || styles.draft;
    return <Badge className={style.class}>{style.label}</Badge>;
  };

  const getProductLabel = (type) => {
    const labels = {
      agglo_10: 'Agglo de 10',
      agglo_15: 'Agglo de 15',
      agglo_20: 'Agglo de 20',
      agglo_hourdis_16: 'Agglo Hourdis 16',
      agglo_plein_15: 'Agglo Plein 15',
      granulat: 'Granulat',
      transport: 'Transport',
      autre: 'Autre'
    };
    return labels[type] || type;
  };

  // Compute delivery progress for a devis: {delivered, total, blsCount, isPartial, isFull}
  const getDevisDeliveryProgress = (devis) => {
    if (!devis || devis.type !== 'devis') return null;
    const childBls = transactions.filter(c => c.parent_id === devis.id && c.type === 'bl' && c.status !== 'cancelled');
    const total = (devis.items || []).reduce((sum, it) => sum + (it.quantity || 0), 0);
    let delivered = 0;
    childBls.forEach(bl => {
      (bl.items || []).forEach(it => { delivered += (it.quantity || 0); });
    });
    if (total <= 0) return null;
    return {
      delivered,
      total,
      blsCount: childBls.length,
      isPartial: delivered > 0 && delivered < total,
      isFull: delivered >= total - 0.001,
      percent: Math.min(100, Math.round((delivered / total) * 100)),
    };
  };

  // Devis validés par le client avec au moins une ligne encore à livrer
  const toConvertDevis = transactions.filter(t => {
    if (t.type !== 'devis' || !t.client_validated_at) return false;
    const childBls = transactions.filter(c => c.parent_id === t.id && c.type === 'bl' && c.status !== 'cancelled');
    if (childBls.length === 0) return true;
    const deliveredByKey = {};
    childBls.forEach(bl => {
      (bl.items || []).forEach(it => {
        const key = (it.description || '').trim().toLowerCase();
        deliveredByKey[key] = (deliveredByKey[key] || 0) + (it.quantity || 0);
      });
    });
    return (t.items || []).some(it => {
      const key = (it.description || '').trim().toLowerCase();
      return (it.quantity || 0) - (deliveredByKey[key] || 0) > 0.001;
    });
  });

  const filteredTransactions = transactions.filter(t => {
    if (activeTab === 'to_convert') {
      return toConvertDevis.some(d => d.id === t.id) &&
        ((t.reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
         (t.client_name || '').toLowerCase().includes(searchTerm.toLowerCase()));
    }
    return t.type === activeTab &&
      ((t.reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
       (t.client_name || '').toLowerCase().includes(searchTerm.toLowerCase()));
  });

  const { subtotal, taxAmount, total } = calculateTotals();

  // Stats
  const stats = {
    devis: transactions.filter(t => t.type === 'devis'),
    bl: transactions.filter(t => t.type === 'bl'),
    factures: transactions.filter(t => t.type === 'facture'),
    unpaid: transactions.filter(t => t.type === 'facture' && t.status !== 'paid')
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="sales-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#1B3268] tracking-tight flex items-center gap-3">
            <Receipt className="w-10 h-10" />
            Gestion des Ventes
          </h1>
          <p className="text-sm text-slate-600 mt-1">Devis, Bons de Livraison et Factures</p>
        </div>
        <Button
          onClick={() => openCreateModal(activeTab === 'to_convert' ? 'devis' : activeTab)}
          data-testid="create-transaction-btn"
          className="bg-[#1B3268] hover:bg-[#152a55] text-white font-bold rounded-sm shadow-lg h-12 px-6"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nouveau {getTypeLabel(activeTab === 'to_convert' ? 'devis' : activeTab)}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-sm">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8" />
            <div>
              <div className="text-sm text-white/70">Devis</div>
              <div className="text-2xl font-bold">{stats.devis.length}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-sm">
          <div className="flex items-center gap-3">
            <Truck className="w-8 h-8" />
            <div>
              <div className="text-sm text-white/70">Bons de Livraison</div>
              <div className="text-2xl font-bold">{stats.bl.length}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-sm">
          <div className="flex items-center gap-3">
            <Receipt className="w-8 h-8" />
            <div>
              <div className="text-sm text-white/70">Factures</div>
              <div className="text-2xl font-bold">{stats.factures.length}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-sm">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8" />
            <div>
              <div className="text-sm text-white/70">En attente paiement</div>
              <div className="text-2xl font-bold">{stats.unpaid.length}</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 mb-4">
          <TabsTrigger value="devis" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Devis
          </TabsTrigger>
          <TabsTrigger value="to_convert" className="flex items-center gap-2 relative" data-testid="tab-to-convert">
            <ArrowRight className="w-4 h-4" />
            <span>À convertir</span>
            {toConvertDevis.length > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold bg-green-500 text-white rounded-full animate-pulse" data-testid="to-convert-count">
                {toConvertDevis.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="bl" className="flex items-center gap-2">
            <Truck className="w-4 h-4" />
            <span className="hidden md:inline">Bons de Livraison</span>
            <span className="md:hidden">BL</span>
          </TabsTrigger>
          <TabsTrigger value="facture" className="flex items-center gap-2">
            <Receipt className="w-4 h-4" />
            Factures
          </TabsTrigger>
        </TabsList>

        {/* Search */}
        <Card className="p-4 mb-4 bg-white border border-slate-200 rounded-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              placeholder="Rechercher par référence ou client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-slate-300"
            />
          </div>
        </Card>

        {/* Table */}
        <Card className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-100 text-slate-700 font-semibold uppercase text-xs tracking-wider">
                  <th className="p-4 text-left">Référence</th>
                  <th className="p-4 text-left">Client</th>
                  <th className="p-4 text-left">Date</th>
                  <th className="p-4 text-right">Montant</th>
                  <th className="p-4 text-center">Statut</th>
                  <th className="p-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((trans) => (
                  <tr 
                    key={trans.id} 
                    className="hover:bg-blue-50 transition-colors border-b last:border-0 cursor-pointer group"
                    onClick={() => openDetailModal(trans)}
                    data-testid={`transaction-row-${trans.reference}`}
                  >
                    <td className="p-4 font-mono font-medium text-[#1B3268] group-hover:text-blue-600">{trans.reference}</td>
                    <td className="p-4">
                      <div className="font-medium">{trans.client_name}</div>
                      {trans.chantier && <div className="text-xs text-slate-500">{trans.chantier}</div>}
                    </td>
                    <td className="p-4 text-slate-600">
                      {new Date(trans.created_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="p-4 text-right font-bold">{(trans.total || 0).toLocaleString('fr-FR')} FCFA</td>
                    <td className="p-4 text-center">
                      <div className="flex flex-col items-center gap-1">
                        {getStatusBadge(trans.status)}
                        {trans.type === 'devis' && trans.client_validated_at && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-green-500 text-white rounded-full animate-pulse"
                            title={`Validé par le client le ${new Date(trans.client_validated_at).toLocaleDateString('fr-FR')}`}
                            data-testid={`validated-badge-${trans.reference}`}
                          >
                            <Check className="w-3 h-3" /> Validé client
                          </span>
                        )}
                        {(() => {
                          const p = getDevisDeliveryProgress(trans);
                          if (!p || p.blsCount === 0) return null;
                          const styles = p.isFull
                            ? 'bg-green-100 text-green-800 border border-green-300'
                            : 'bg-blue-100 text-blue-800 border border-blue-300';
                          return (
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full ${styles}`}
                              title={`${p.blsCount} BL créé${p.blsCount > 1 ? 's' : ''} • ${p.delivered.toLocaleString('fr-FR')}/${p.total.toLocaleString('fr-FR')} livrés (${p.percent}%)`}
                              data-testid={`delivery-progress-${trans.reference}`}
                            >
                              <Truck className="w-3 h-3" />
                              {p.delivered.toLocaleString('fr-FR')}/{p.total.toLocaleString('fr-FR')} livrés
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openDetailModal(trans)} title="Voir détails" className="text-blue-600">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDuplicate(trans)} title="Dupliquer" className="text-purple-600">
                          <Copy className="w-4 h-4" />
                        </Button>
                        {trans.type === 'devis' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openConvertModal(trans, 'bl')}
                            title="Convertir en Bon de Livraison"
                            className="text-green-600"
                            data-testid={`convert-devis-row-${trans.reference}`}
                          >
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        )}
                        {trans.type === 'bl' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openConvertModal(trans, 'facture')}
                            title="Convertir en Facture"
                            className="text-green-600"
                          >
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(trans)} title="Télécharger PDF">
                          <Download className="w-4 h-4" />
                        </Button>
                        {trans.client_phone && (
                          <Button variant="ghost" size="sm" onClick={() => handleWhatsApp(trans)} title="Envoyer par WhatsApp">
                            <Phone className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => setTransactionToDelete(trans)} className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredTransactions.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                {activeTab === 'to_convert'
                  ? '🎉 Aucun devis validé en attente de conversion. Tout est à jour !'
                  : `Aucun ${getTypeLabel(activeTab).toLowerCase()} trouvé`}
              </div>
            )}
          </div>
        </Card>
      </Tabs>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1B3268]">
              Nouveau {getTypeLabel(formData.type)}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Client & Type & Date */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select value={formData.client_id} onValueChange={(v) => setFormData({...formData, client_id: v})}>
                  <SelectTrigger data-testid="create-trans-client-select"><SelectValue placeholder="Sélectionner un client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type de comptabilité</Label>
                <Select value={formData.accounting_type} onValueChange={(v) => setFormData({...formData, accounting_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">Type A (Déclaré)</SelectItem>
                    <SelectItem value="B">Type B (Non déclaré)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date du document *</Label>
                <Input
                  type="date"
                  data-testid="create-trans-date-input"
                  value={formData.document_date}
                  onChange={(e) => setFormData({...formData, document_date: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Chantier / Site</Label>
              <Input
                placeholder="Ex: CHANTIER DIAWARA"
                value={formData.chantier}
                onChange={(e) => setFormData({...formData, chantier: e.target.value})}
              />
            </div>

            {/* Titre / Référence */}
            <div className="space-y-2">
              <Label>Titre / Référence du document</Label>
              <Input
                placeholder="Ex: Livraison sable pour fondation, Commande n°123..."
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
              />
              <p className="text-xs text-slate-500">Ce titre apparaîtra sur le document PDF</p>
            </div>

            {/* Sale Category */}
            <div className="space-y-2">
              <Label>Catégorie de vente *</Label>
              <div className="flex gap-4 p-3 bg-slate-50 rounded-md">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="sale_category"
                    value="agglo"
                    checked={formData.sale_category === 'agglo'}
                    onChange={(e) => setFormData({...formData, sale_category: e.target.value})}
                    className="w-4 h-4 text-[#1B3268]"
                  />
                  <span className="font-medium text-[#1B3268]">Agglo</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="sale_category"
                    value="granulats"
                    checked={formData.sale_category === 'granulats'}
                    onChange={(e) => setFormData({...formData, sale_category: e.target.value})}
                    className="w-4 h-4 text-amber-500"
                  />
                  <span className="font-medium text-amber-600">Granulats</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="sale_category"
                    value="autres"
                    checked={formData.sale_category === 'autres'}
                    onChange={(e) => setFormData({...formData, sale_category: e.target.value})}
                    className="w-4 h-4 text-slate-500"
                  />
                  <span className="font-medium text-slate-600">Autres</span>
                </label>
              </div>
              {formData.sale_category === 'granulats' && (
                <p className="text-xs text-amber-600">
                  Cette vente apparaîtra dans l'onglet Granulats pour le suivi des marges
                </p>
              )}
            </div>

            {/* Items */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Articles</Label>
                <Button variant="outline" size="sm" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-1" /> Ajouter
                </Button>
              </div>
              
              {formData.items.map((item, index) => (
                <div key={index} className="p-3 bg-slate-50 rounded-md space-y-2">
                  {/* Product Selection */}
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-4">
                      <Label className="text-xs text-slate-500">Source</Label>
                      <Select value={item.product_id || 'manual'} onValueChange={(v) => updateItem(index, 'product_id', v === 'manual' ? '' : v)}>
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Choisir..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">Saisie libre</SelectItem>
                          {productsServices.filter(p => p.type === 'produit').length > 0 && (
                            <>
                              <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-100">Produits</div>
                              {productsServices.filter(p => p.type === 'produit').map(p => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name} - {p.unit_price.toLocaleString('fr-FR')} FCFA/{p.unit}
                                </SelectItem>
                              ))}
                            </>
                          )}
                          {productsServices.filter(p => p.type === 'service').length > 0 && (
                            <>
                              <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-100">Services</div>
                              {productsServices.filter(p => p.type === 'service').map(p => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name} - {p.unit_price.toLocaleString('fr-FR')} FCFA/{p.unit}
                                </SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-8">
                      <Label className="text-xs text-slate-500">
                        {item.product_id ? 'Description (auto-remplie)' : 'Produit / Service (texte libre)'}
                      </Label>
                      <Input
                        placeholder={item.product_id ? "Description additionnelle..." : "Ex: Sable 0/4, Gravier 4/8, Transport..."}
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        className="h-9"
                      />
                    </div>
                  </div>
                  {/* Quantity, Price, Total */}
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-3">
                      <Label className="text-xs text-slate-500">Quantité</Label>
                      <Input
                        type="number"
                        placeholder="Qté"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-slate-500">Unité</Label>
                      <Input
                        value={item.unit}
                        onChange={(e) => updateItem(index, 'unit', e.target.value)}
                        className="h-9"
                        readOnly={!!item.product_id}
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs text-slate-500">Prix unit. (FCFA)</Label>
                      <Input
                        type="number"
                        placeholder="Prix unit."
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    {/* Coût d'achat pour ventes granulats */}
                    {formData.sale_category === 'granulats' && (
                      <div className="col-span-3">
                        <Label className="text-xs text-amber-600">Coût achat unit.</Label>
                        <Input
                          type="number"
                          placeholder="Coût d'achat"
                          value={item.purchase_cost || ''}
                          onChange={(e) => updateItem(index, 'purchase_cost', e.target.value)}
                          className="h-9 border-amber-300 focus:border-amber-500"
                        />
                      </div>
                    )}
                    <div className={`${formData.sale_category === 'granulats' ? 'col-span-2' : 'col-span-3'} flex flex-col justify-end`}>
                      <Label className="text-xs text-slate-500">Total</Label>
                      <div className="h-9 flex items-center font-bold text-[#1B3268]">
                        {(item.total || 0).toLocaleString('fr-FR')} FCFA
                      </div>
                    </div>
                    <div className="col-span-1 flex items-end justify-center pb-1">
                      {formData.items.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeItem(index)} className="text-red-500 h-9 w-9 p-0">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="bg-slate-100 p-4 rounded-md space-y-2">
              <div className="flex justify-between">
                <span>Sous-total HT:</span>
                <span className="font-mono">{(subtotal || 0).toLocaleString('fr-FR')} FCFA</span>
              </div>
              <div className="flex justify-between items-center">
                <span>TVA:</span>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    className="w-20 h-8"
                    value={formData.tax_rate}
                    onChange={(e) => setFormData({...formData, tax_rate: parseFloat(e.target.value) || 0})}
                  />
                  <span>%</span>
                  <span className="font-mono ml-4">{(taxAmount || 0).toLocaleString('fr-FR')} FCFA</span>
                </div>
              </div>
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Total TTC:</span>
                <span className="text-[#1B3268]">{(total || 0).toLocaleString('fr-FR')} FCFA</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Notes ou conditions particulières..."
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
              />
            </div>
          </div>

          <DialogFooter className="sticky bottom-0 bg-white pt-4 border-t mt-4 -mb-2">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1B3268] hover:bg-[#152a55] text-white">
              {saving ? 'Enregistrement...' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1B3268]">
              <CreditCard className="w-5 h-5 inline mr-2" />
              Enregistrer le paiement
            </DialogTitle>
          </DialogHeader>
          
          {transactionForPayment && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-3 rounded-md">
                <p><strong>Facture:</strong> {transactionForPayment.reference}</p>
                <p><strong>Client:</strong> {transactionForPayment.client_name}</p>
                <p><strong>Montant:</strong> {(transactionForPayment?.total || 0).toLocaleString('fr-FR')} FCFA</p>
              </div>
              
              <div className="space-y-2">
                <Label>Mode de paiement</Label>
                <Select value={paymentData.payment_method} onValueChange={(v) => setPaymentData({...paymentData, payment_method: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="especes">Espèces</SelectItem>
                    <SelectItem value="virement">Virement bancaire</SelectItem>
                    <SelectItem value="mobile_money">Mobile Money</SelectItem>
                    <SelectItem value="cheque">Chèque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Date de paiement</Label>
                <Input
                  type="date"
                  value={paymentData.payment_date}
                  onChange={(e) => setPaymentData({...paymentData, payment_date: e.target.value})}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>Annuler</Button>
            <Button onClick={handleMarkAsPaid} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Check className="w-4 h-4 mr-2" /> Confirmer paiement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1B3268] flex items-center gap-2">
              <Edit className="w-6 h-6" />
              Modifier {transactionToEdit && getTypeLabel(transactionToEdit.type)} {transactionToEdit?.reference}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Client & Type & Date */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select value={editFormData.client_id} onValueChange={(v) => setEditFormData({...editFormData, client_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner un client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Type de comptabilité</Label>
                <Select value={editFormData.accounting_type} onValueChange={(v) => setEditFormData({...editFormData, accounting_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">Type A (Déclaré)</SelectItem>
                    <SelectItem value="B">Type B (Non déclaré)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date du document *</Label>
                <Input
                  type="date"
                  data-testid="edit-trans-date-input"
                  value={editFormData.document_date}
                  onChange={(e) => setEditFormData({...editFormData, document_date: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Chantier / Site</Label>
              <Input
                placeholder="Ex: CHANTIER DIAWARA"
                value={editFormData.chantier}
                onChange={(e) => setEditFormData({...editFormData, chantier: e.target.value})}
              />
            </div>

            {/* Items */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Articles</Label>
                <Button variant="outline" size="sm" onClick={addEditItem}>
                  <Plus className="w-4 h-4 mr-1" /> Ajouter
                </Button>
              </div>
              
              {editFormData.items.map((item, index) => (
                <div key={index} className="p-3 bg-slate-50 rounded-md space-y-2">
                  {/* Product Selection */}
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-4">
                      <Label className="text-xs text-slate-500">Source</Label>
                      <Select value={item.product_id || 'manual'} onValueChange={(v) => updateEditItemWithProduct(index, 'product_id', v === 'manual' ? '' : v)}>
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Choisir..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">Saisie libre</SelectItem>
                          {productsServices.filter(p => p.type === 'produit').length > 0 && (
                            <>
                              <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-100">Produits</div>
                              {productsServices.filter(p => p.type === 'produit').map(p => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name} - {p.unit_price.toLocaleString('fr-FR')} FCFA/{p.unit}
                                </SelectItem>
                              ))}
                            </>
                          )}
                          {productsServices.filter(p => p.type === 'service').length > 0 && (
                            <>
                              <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-100">Services</div>
                              {productsServices.filter(p => p.type === 'service').map(p => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name} - {p.unit_price.toLocaleString('fr-FR')} FCFA/{p.unit}
                                </SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-8">
                      <Label className="text-xs text-slate-500">
                        {item.product_id ? 'Description (auto-remplie)' : 'Produit / Service (texte libre)'}
                      </Label>
                      <Input
                        placeholder={item.product_id ? "Description additionnelle..." : "Ex: Agglo spécial, Transport Dakar-Thiès..."}
                        value={item.description}
                        onChange={(e) => updateEditItemWithProduct(index, 'description', e.target.value)}
                        className="h-9"
                      />
                    </div>
                  </div>
                  {/* Quantity, Price, Total */}
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-3">
                      <Label className="text-xs text-slate-500">Quantité</Label>
                      <Input
                        type="number"
                        placeholder="Qté"
                        value={item.quantity}
                        onChange={(e) => updateEditItemWithProduct(index, 'quantity', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-slate-500">Unité</Label>
                      <Input
                        value={item.unit}
                        onChange={(e) => updateEditItemWithProduct(index, 'unit', e.target.value)}
                        className="h-9"
                        readOnly={!!item.product_id}
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs text-slate-500">Prix unit. (FCFA)</Label>
                      <Input
                        type="number"
                        placeholder="Prix unit."
                        value={item.unit_price}
                        onChange={(e) => updateEditItemWithProduct(index, 'unit_price', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div className="col-span-3 flex flex-col justify-end">
                      <Label className="text-xs text-slate-500">Total</Label>
                      <div className="h-9 flex items-center font-bold text-[#1B3268]">
                        {(item.total || 0).toLocaleString('fr-FR')} FCFA
                      </div>
                    </div>
                    <div className="col-span-1 flex items-end justify-center pb-1">
                      {editFormData.items.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeEditItem(index)} className="text-red-500 h-9 w-9 p-0">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            {(() => {
              const { subtotal, taxAmount, total } = calculateEditTotals();
              return (
                <div className="bg-slate-100 p-4 rounded-md space-y-2">
                  <div className="flex justify-between">
                    <span>Sous-total HT:</span>
                    <span className="font-mono">{(subtotal || 0).toLocaleString('fr-FR')} FCFA</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>TVA:</span>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        className="w-20 h-8"
                        value={editFormData.tax_rate}
                        onChange={(e) => setEditFormData({...editFormData, tax_rate: parseFloat(e.target.value) || 0})}
                      />
                      <span>%</span>
                      <span className="font-mono ml-4">{(taxAmount || 0).toLocaleString('fr-FR')} FCFA</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Total TTC:</span>
                    <span className="text-[#1B3268]">{(total || 0).toLocaleString('fr-FR')} FCFA</span>
                  </div>
                </div>
              );
            })()}

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Notes ou conditions particulières..."
                value={editFormData.notes}
                onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
              />
            </div>
          </div>

          <DialogFooter className="sticky bottom-0 bg-white pt-4 border-t mt-4 -mb-2">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Annuler</Button>
            <Button onClick={handleEditSave} disabled={saving} className="bg-[#1B3268] hover:bg-[#152a55] text-white">
              {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!transactionToDelete} onOpenChange={() => setTransactionToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Supprimer ce document ?</AlertDialogTitle>
            <AlertDialogDescription>
              {transactionToDelete && (
                <p>Voulez-vous supprimer le {getTypeLabel(transactionToDelete.type)} <strong>{transactionToDelete.reference}</strong> ?</p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Convert Modal with Choice */}
      <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
        <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1B3268] flex items-center gap-2">
              <ArrowRight className="w-5 h-5" />
              Convertir le document
            </DialogTitle>
          </DialogHeader>
          
          {transactionToConvert && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-3 rounded-md text-sm">
                <p><strong>Document:</strong> {transactionToConvert.reference}</p>
                <p><strong>Type:</strong> {getTypeLabel(transactionToConvert.type)}</p>
                <p><strong>Client:</strong> {transactionToConvert.client_name}</p>
                {convertTargetType !== 'bl' && (
                  <p><strong>Montant:</strong> {(transactionToConvert?.total || 0).toLocaleString('fr-FR')} FCFA</p>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Convertir en :</Label>
                  <Select value={convertTargetType} onValueChange={(v) => { setConvertTargetType(v); if (v==='bl' && transactionToConvert.type==='devis') openConvertModal(transactionToConvert, 'bl'); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {transactionToConvert.type === 'devis' && (
                        <>
                          <SelectItem value="bl">
                            <div className="flex items-center gap-2"><Truck className="w-4 h-4" /> Bon de Livraison</div>
                          </SelectItem>
                          <SelectItem value="facture">
                            <div className="flex items-center gap-2"><Receipt className="w-4 h-4" /> Facture</div>
                          </SelectItem>
                        </>
                      )}
                      {transactionToConvert.type === 'bl' && (
                        <SelectItem value="facture">
                          <div className="flex items-center gap-2"><Receipt className="w-4 h-4" /> Facture</div>
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date du document *</Label>
                  <Input type="date" data-testid="convert-date-input" value={convertDate} onChange={(e) => setConvertDate(e.target.value)} />
                </div>
              </div>

              {/* Partial delivery UI for devis → BL */}
              {convertTargetType === 'bl' && transactionToConvert.type === 'devis' && deliveryStatus && (
                <div className="border rounded-md">
                  <div className="bg-slate-100 p-3 flex items-center justify-between text-sm">
                    <div>
                      <strong>Suivi de livraison du devis</strong>
                      <span className="text-slate-500 ml-2">
                        {deliveryStatus.child_bls_count} BL déjà créé{deliveryStatus.child_bls_count > 1 ? 's' : ''}
                      </span>
                    </div>
                    {deliveryStatus.fully_delivered && (
                      <span className="text-orange-600 font-semibold text-xs">⚠️ Tout est déjà livré</span>
                    )}
                  </div>
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="p-2 text-left">Description</th>
                        <th className="p-2 text-right">Qté devis</th>
                        <th className="p-2 text-right">Déjà livrée</th>
                        <th className="p-2 text-right">Restant</th>
                        <th className="p-2 text-right w-[110px]">Qté à livrer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {convertItems.map((it, idx) => (
                        <tr key={idx} className={`border-t ${it.remaining <= 0 ? 'opacity-50 bg-slate-50' : ''}`}>
                          <td className="p-2">
                            <div className="font-medium">{it.description}</div>
                            <div className="text-[10px] text-slate-500">{it.unit}</div>
                          </td>
                          <td className="p-2 text-right">{(it.quantity || 0).toLocaleString('fr-FR')}</td>
                          <td className="p-2 text-right text-purple-700">{(it.delivered || 0).toLocaleString('fr-FR')}</td>
                          <td className="p-2 text-right font-semibold text-green-700">{(it.remaining || 0).toLocaleString('fr-FR')}</td>
                          <td className="p-2">
                            <Input
                              type="number"
                              min="0"
                              max={it.remaining}
                              step="0.01"
                              value={it.qty_now}
                              data-testid={`convert-qty-${idx}`}
                              onChange={(e) => {
                                const v = e.target.value;
                                setConvertItems(prev => prev.map((p, i) => i === idx ? { ...p, qty_now: v } : p));
                              }}
                              className="h-8 text-right"
                              disabled={it.remaining <= 0}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="p-2 flex justify-end gap-2 bg-slate-50 border-t text-xs">
                    <Button size="sm" variant="outline" type="button" data-testid="deliver-all-btn" onClick={() => setConvertItems(prev => prev.map(p => ({ ...p, qty_now: p.remaining })))}>
                      Tout livrer
                    </Button>
                    <Button size="sm" variant="outline" type="button" data-testid="clear-quantities-btn" onClick={() => setConvertItems(prev => prev.map(p => ({ ...p, qty_now: 0 })))}>
                      Vider
                    </Button>
                  </div>
                </div>
              )}

              {convertTargetType === 'bl' && (
                <p className="text-sm text-slate-500">
                  ℹ️ Les prix ne figureront pas sur le PDF du BL. Plusieurs BL peuvent être créés à partir d'un même devis (livraisons partielles).
                </p>
              )}
              {convertTargetType === 'facture' && (
                <p className="text-sm text-slate-500">
                  Une facture sera créée avec une échéance de 30 jours.
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertModal(false)}>Annuler</Button>
            <Button onClick={handleConvert} data-testid="convert-submit-btn" className="bg-[#1B3268] hover:bg-[#152a55] text-white">
              <ArrowRight className="w-4 h-4 mr-2" /> Convertir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal - View and Edit Document */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-[900px] max-h-[95vh] overflow-y-auto p-0">
          {selectedTransaction && (
            <>
              {/* Header with gradient */}
              <div className="bg-gradient-to-r from-[#1B3268] to-[#2a4a8f] text-white p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      {selectedTransaction.type === 'devis' && <FileText className="w-8 h-8" />}
                      {selectedTransaction.type === 'bl' && <Truck className="w-8 h-8" />}
                      {selectedTransaction.type === 'facture' && <Receipt className="w-8 h-8" />}
                      <div>
                        <h2 className="text-2xl font-bold">{getTypeLabel(selectedTransaction.type)}</h2>
                        <p className="text-blue-200 font-mono">{selectedTransaction.reference}</p>
                      </div>
                    </div>
                    <p className="text-blue-200 text-sm">
                      Créé le {new Date(selectedTransaction.created_at).toLocaleDateString('fr-FR', { 
                        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(selectedTransaction.status)}
                    {selectedTransaction.client_validated_at && (
                      <div className="mt-2 flex items-center gap-1 text-green-300 text-sm">
                        <Check className="w-4 h-4" />
                        Validé par le client
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons Bar */}
              <div className="bg-slate-50 border-b px-6 py-3 flex flex-wrap gap-2">
                {!isEditing ? (
                  <>
                    <Button size="sm" onClick={() => setIsEditing(true)} className="bg-blue-600 hover:bg-blue-700">
                      <Edit className="w-4 h-4 mr-1" /> Modifier
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDownloadPDF(selectedTransaction)}>
                      <Download className="w-4 h-4 mr-1" /> PDF
                    </Button>
                    {selectedTransaction.client_email && (
                      <Button size="sm" variant="outline" onClick={() => handleSendEmail(selectedTransaction)}>
                        <Mail className="w-4 h-4 mr-1" /> Email
                      </Button>
                    )}
                    {selectedTransaction.client_phone && (
                      <Button size="sm" variant="outline" onClick={() => handleWhatsApp(selectedTransaction)}>
                        <Phone className="w-4 h-4 mr-1" /> WhatsApp
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => { handleDuplicate(selectedTransaction); setShowDetailModal(false); }}>
                      <Copy className="w-4 h-4 mr-1" /> Dupliquer
                    </Button>
                    {selectedTransaction.type === 'devis' && (
                      <Button size="sm" variant="outline" className="text-green-600 border-green-600" onClick={() => { openConvertModal(selectedTransaction); setShowDetailModal(false); }} data-testid="convert-devis-to-bl-btn">
                        <ArrowRight className="w-4 h-4 mr-1" /> Convertir en BL
                      </Button>
                    )}
                    {selectedTransaction.type === 'bl' && (
                      <Button size="sm" variant="outline" className="text-green-600 border-green-600" onClick={() => { openConvertModal(selectedTransaction, 'facture'); setShowDetailModal(false); }} data-testid="convert-bl-to-facture-btn">
                        <ArrowRight className="w-4 h-4 mr-1" /> Convertir en Facture
                      </Button>
                    )}
                    {selectedTransaction.type === 'facture' && selectedTransaction.status !== 'paid' && (
                      <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-600" onClick={() => { setTransactionForPayment(selectedTransaction); setShowPaymentModal(true); setShowDetailModal(false); }}>
                        <CreditCard className="w-4 h-4 mr-1" /> Marquer Payé
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <Button size="sm" onClick={handleSaveFromDetail} disabled={saving} className="bg-green-600 hover:bg-green-700">
                      <Check className="w-4 h-4 mr-1" /> {saving ? 'Sauvegarde...' : 'Enregistrer'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                      Annuler
                    </Button>
                  </>
                )}
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Client and Details */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-700 border-b pb-2">Client</h3>
                    {!isEditing ? (
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <p className="font-bold text-lg">{selectedTransaction.client_name}</p>
                        {selectedTransaction.client_address && <p className="text-slate-600">{selectedTransaction.client_address}</p>}
                        {selectedTransaction.client_phone && <p className="text-slate-600">{selectedTransaction.client_phone}</p>}
                        {selectedTransaction.client_email && <p className="text-slate-600">{selectedTransaction.client_email}</p>}
                      </div>
                    ) : (
                      <Select value={editFormData.client_id} onValueChange={(v) => setEditFormData({...editFormData, client_id: v})}>
                        <SelectTrigger><SelectValue placeholder="Sélectionner un client" /></SelectTrigger>
                        <SelectContent>
                          {clients.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-700 border-b pb-2">Détails</h3>
                    {!isEditing ? (
                      <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Chantier:</span>
                          <span className="font-medium">{selectedTransaction.chantier || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Type comptable:</span>
                          <Badge variant={selectedTransaction.accounting_type === 'A' ? 'default' : 'secondary'}>
                            Type {selectedTransaction.accounting_type}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">TVA:</span>
                          <span className="font-medium">{selectedTransaction.tax_rate}%</span>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Input
                          placeholder="Chantier / Site"
                          value={editFormData.chantier}
                          onChange={(e) => setEditFormData({...editFormData, chantier: e.target.value})}
                        />
                        <Select value={editFormData.accounting_type} onValueChange={(v) => setEditFormData({...editFormData, accounting_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="A">Type A (Déclaré)</SelectItem>
                            <SelectItem value="B">Type B (Non déclaré)</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          placeholder="TVA %"
                          value={editFormData.tax_rate}
                          onChange={(e) => setEditFormData({...editFormData, tax_rate: e.target.value})}
                        />
                        <div>
                          <Label className="text-xs text-slate-500">Date du document</Label>
                          <Input
                            type="date"
                            data-testid="detail-trans-date-input"
                            value={editFormData.document_date}
                            onChange={(e) => setEditFormData({...editFormData, document_date: e.target.value})}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Items */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b pb-2">
                    <h3 className="font-semibold text-slate-700">Articles</h3>
                    {isEditing && (
                      <Button size="sm" variant="outline" onClick={addEditItem}>
                        <Plus className="w-4 h-4 mr-1" /> Ajouter
                      </Button>
                    )}
                  </div>
                  
                  {!isEditing ? (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-[#1B3268] text-white">
                          <tr>
                            <th className="p-3 text-left">Description</th>
                            <th className="p-3 text-center">Quantité</th>
                            <th className="p-3 text-right">Prix unit.</th>
                            <th className="p-3 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedTransaction.items.map((item, idx) => (
                            <tr key={idx} className={idx % 2 === 1 ? 'bg-slate-50' : ''}>
                              <td className="p-3 font-medium">{item.description || item.product_type}</td>
                              <td className="p-3 text-center">{item.quantity} {item.unit}</td>
                              <td className="p-3 text-right">{(item.unit_price || 0).toLocaleString('fr-FR')} FCFA</td>
                              <td className="p-3 text-right font-bold">{(item.total || 0).toLocaleString('fr-FR')} FCFA</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {editFormData.items.map((item, index) => (
                        <div key={index} className="p-3 bg-slate-50 rounded-lg space-y-2 border">
                          <div className="grid grid-cols-12 gap-2">
                            <div className="col-span-5">
                              <Label className="text-xs">Description</Label>
                              <Input
                                placeholder="Article..."
                                value={item.description}
                                onChange={(e) => updateEditItemWithProduct(index, 'description', e.target.value)}
                              />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">Quantité</Label>
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateEditItemWithProduct(index, 'quantity', e.target.value)}
                              />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">Unité</Label>
                              <Input
                                value={item.unit}
                                onChange={(e) => updateEditItemWithProduct(index, 'unit', e.target.value)}
                              />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">Prix unit.</Label>
                              <Input
                                type="number"
                                value={item.unit_price}
                                onChange={(e) => updateEditItemWithProduct(index, 'unit_price', e.target.value)}
                              />
                            </div>
                            <div className="col-span-1 flex items-end">
                              {editFormData.items.length > 1 && (
                                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeEditItem(index)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <div className="text-right text-sm font-bold text-[#1B3268]">
                            Total: {((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)).toLocaleString('fr-FR')} FCFA
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Totals */}
                <div className="bg-slate-100 p-4 rounded-lg">
                  <div className="max-w-xs ml-auto space-y-2">
                    <div className="flex justify-between text-slate-600">
                      <span>Sous-total HT:</span>
                      <span>{(isEditing 
                        ? editFormData.items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0), 0)
                        : selectedTransaction.subtotal
                      ).toLocaleString('fr-FR')} FCFA</span>
                    </div>
                    {(selectedTransaction.tax_rate > 0 || (isEditing && parseFloat(editFormData.tax_rate) > 0)) && (
                      <div className="flex justify-between text-slate-600">
                        <span>TVA ({isEditing ? editFormData.tax_rate : selectedTransaction.tax_rate}%):</span>
                        <span>{(isEditing 
                          ? editFormData.items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0), 0) * (parseFloat(editFormData.tax_rate) / 100)
                          : selectedTransaction.tax_amount
                        ).toLocaleString('fr-FR')} FCFA</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xl font-bold border-t pt-2">
                      <span>Total TTC:</span>
                      <span className="text-[#1B3268]">{(isEditing 
                        ? (() => {
                            const subtotal = editFormData.items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0), 0);
                            return subtotal + (subtotal * (parseFloat(editFormData.tax_rate) || 0) / 100);
                          })()
                        : selectedTransaction.total
                      ).toLocaleString('fr-FR')} FCFA</span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {(selectedTransaction.notes || isEditing) && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-slate-700 border-b pb-2">Notes</h3>
                    {!isEditing ? (
                      <p className="text-slate-600 bg-slate-50 p-3 rounded-lg">{selectedTransaction.notes || '-'}</p>
                    ) : (
                      <Textarea
                        placeholder="Notes ou conditions particulières..."
                        value={editFormData.notes}
                        onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                      />
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesPage;
